import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import AdvertiserDetailsSection from "./AdvertiserDetailsSection";
import Breadcrumps from "../../components/modules/Breadcrumps";
import CampaignsDashboard from "./CampaignsDashboard";
import CampaignsDialog from "./CampaignsDialog";
import CampaignsDialogControl from "./CampaignsDialogControl";
import Loader from "../../components/elements/Loader";
import PageLayout from "../../layouts/PageLayout";
import api from "../../api";
import classes from "./AdvertiserDetailPage.module.scss";
import { AdvertiserType } from "../../types/advertiserTypes";
import { CamapignReportType, CampaignType, ReportTimeFrames } from "../../types/campaignTypes";
import { ReduxApplicationState } from "../../types/ReduxTypes/ReduxApplicationState";
import { setCampaignToCreateSelectedAdvertiser } from "../../redux/reducers/campaignToCreate";
import { truncateText } from "../../helpers/truncateText";

const fetchAdvertiserById = async (bgAdvertiserId: string) => {
  try {
    const { payload } = await api.getAdvertiserById(bgAdvertiserId);
    return payload;
  } catch (error) {
    const err = error as any;
    throw new Error(err.message);
  }
};

const fetchAdvertiserCampaigns = async (bgAdvertiserId: string, timeFrame: string) => {
  try {
    const { payload } = await api.getAllCampaignsByAdvertiser(bgAdvertiserId, timeFrame);
    return payload;
  } catch (error) {
    const err = error as any;
    throw new Error(err.message);
  }
};

const fetchCampaignDailyReport = async (bgCampaignd: string, date: Date, beeswaxCampaign: number) => {
  try {
    const { payload } = await api.getCampaignDailyReport(bgCampaignd, date, beeswaxCampaign);
    return payload;
  } catch (error) {
    const err = error as any;
    throw new Error(err.message);
  }
};

const AdvertiserDetailPage = () => {
  const advertisers = useSelector((state: ReduxApplicationState) => state.agency.advertisers);
  const [isLoadingAdvertiserDetailData, setIsLoadingAdvertiserDetailData] = useState(false);
  const [selectedTimeFrame, setSelectedTimeFrame] = React.useState<ReportTimeFrames>(ReportTimeFrames.ALL_TIME);
  const [advertiserDetailData, setAdvertiserDetailData] = useState<AdvertiserType | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignType[]>([]);
  const [unarchivedCampaigns, setUnarchivedCampaigns] = useState<CampaignType[]>([]);

  const [isCampaignsDialogOpen, setIsCampaignsDialogOpen] = useState(false);
  const [wereCampaignsEdited, setWereCampaignsEdited] = useState(false);

  const [selectedCampaign, setSelectedCampaign] = useState<CampaignType | null>(null);
  const [selectedCampaignReport, setSelectedCampaignReport] = useState<CamapignReportType | null>(null);

  const [isCustomDateChanged, setIsCustomDateChanged] = useState(false);
  const [customReportDate, setCustomReportDate] = React.useState<Date>();

  const params: { id: string } = useParams();
  const mounted = useRef(false);
  const dispatch = useDispatch();

  const [isFetching, setIsFetching] = useState(false);

  const fetchAndSetAdvertiserDetailData = useCallback(async () => {
    try {
      setIsLoadingAdvertiserDetailData(true);
      const fetchedAdvertiserDetailData = await fetchAdvertiserById(params.id);
      setAdvertiserDetailData(fetchedAdvertiserDetailData);
      setIsLoadingAdvertiserDetailData(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("fetchAndSetAdvertiserDetailData error: ", error);
      setIsLoadingAdvertiserDetailData(false);
    }
  }, [params.id]);

  const fetchAndSetCampaigns = useCallback(async () => {
    try {
      if (selectedTimeFrame === ReportTimeFrames.CUSTOM_DATE) return;

      const fetchedAdvertiserCampaigns = (await fetchAdvertiserCampaigns(
        params.id,
        selectedTimeFrame,
      )) as CampaignType[];
      setCampaigns(fetchedAdvertiserCampaigns);
      setUnarchivedCampaigns(fetchedAdvertiserCampaigns.filter((campaign) => !campaign.archived));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("fetchAndSetCampaigns error: ", error);
    }
  }, [params.id, selectedTimeFrame]);

  useEffect(() => {
    fetchAndSetAdvertiserDetailData();
  }, [fetchAndSetAdvertiserDetailData, params.id]);

  useEffect(() => {
    if (!mounted.current) {
      fetchAndSetCampaigns();
      mounted.current = true;
    } else if (wereCampaignsEdited) {
      fetchAndSetCampaigns();
      setWereCampaignsEdited(false);
    }
  }, [fetchAndSetCampaigns, wereCampaignsEdited]);

  useEffect(() => {
    fetchAndSetCampaigns();
  }, [fetchAndSetCampaigns, selectedTimeFrame]);

  const handleOpenCampaignsDialog = useCallback(() => setIsCampaignsDialogOpen(true), []);
  const handleCloseCampaignsDialog = () => setIsCampaignsDialogOpen(false);

  const handleTimeFrameChange = useCallback(
    (
      event: React.ChangeEvent<{
        value: unknown;
        custom_date?: Date;
      }>,
    ) => {
      const {value} = event.target;
      const date = event.target?.custom_date;
      if (
        value === ReportTimeFrames.CUSTOM_DATE &&
        date &&
        new Date(date).getTime() !== new Date(customReportDate ?? "").getTime()
      ) {
        setCustomReportDate(date);
        setIsCustomDateChanged(true);
      } else {
        setIsCustomDateChanged(false);
      }
      setSelectedTimeFrame(value as ReportTimeFrames);
    },
    [customReportDate, setIsCustomDateChanged],
  );

  const breadcrumps = [
    { label: "Home", link: "/dashboard", active: false },
    { label: "Advertisers", link: "/advertisers", active: false },
    { label: truncateText(advertiserDetailData?.name ?? "...", 36), link: `/advertisers/${params.id}`, active: true },
  ];

  useEffect(() => {
    if (advertiserDetailData) dispatch(setCampaignToCreateSelectedAdvertiser(advertiserDetailData));
  }, [advertiserDetailData, dispatch]);

  useEffect(() => {
    const fetchCustomDateCampaignReport = async () => {
      setIsFetching(true);
      if (
        selectedTimeFrame === ReportTimeFrames.CUSTOM_DATE &&
        selectedCampaign &&
        customReportDate &&
        isCustomDateChanged
      ) {
        try {
          const report = await fetchCampaignDailyReport(
            selectedCampaign.brandgenCampaignId,
            customReportDate,
            selectedCampaign.beeswaxCampaignId,
          );
          setSelectedCampaignReport(report);
        } catch (error) {
          console.log("fetchCustomDateCampaignReport error: ", error);
        }
      }
      setIsFetching(false);
    };

    fetchCustomDateCampaignReport();
  }, [selectedCampaign, customReportDate, selectedTimeFrame, isCustomDateChanged]);

  return (
    <PageLayout heading="Account information" heightAuto>
      <Breadcrumps breadcrumps={breadcrumps} className={classes.breadcrumps} />

      {!advertiserDetailData || isLoadingAdvertiserDetailData ? (
        <Loader />
      ) : (
        <>
          <AdvertiserDetailsSection
            fetchAndSetAdvertiserDetailData={fetchAndSetAdvertiserDetailData}
            advertisers={advertisers}
            advertiser={advertiserDetailData}
          />

          <>
            {!unarchivedCampaigns.length && campaigns.length ? (
              <CampaignsDialogControl handleOpenCampaignsDialog={handleOpenCampaignsDialog} />
            ) : unarchivedCampaigns.length ? (
              <CampaignsDashboard
                campaigns={unarchivedCampaigns}
                selectedTimeFrame={selectedTimeFrame}
                handleTimeFrameChange={handleTimeFrameChange}
                fetchAndSetCampaigns={fetchAndSetCampaigns}
                handleOpenCampaignsDialog={handleOpenCampaignsDialog}
                selectedCampaign={selectedCampaign}
                setSelectedCampaign={setSelectedCampaign}
                selectedCampaignReport={selectedCampaignReport}
                customReportDate={customReportDate}
                isFetching={isFetching}
                isCustomDateChanged={isCustomDateChanged}
              />
            ) : null}

            {!campaigns.length ? null : (
              <CampaignsDialog
                isOpen={isCampaignsDialogOpen}
                handleClose={handleCloseCampaignsDialog}
                campaigns={campaigns}
                advertiserName={advertiserDetailData.name}
                setWereCampaignsEdited={setWereCampaignsEdited}
              />
            )}
          </>
        </>
      )}
    </PageLayout>
  );
};

export default AdvertiserDetailPage;
